import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, ArrowUpDown, MapPin } from "@/components/icons";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ImportAssets } from "@/components/ImportAssets";
import { Code } from "@/components/Code";
import { InlineEmpty } from "@/components/ListState";
import { Page } from "@/components/PageConsole";
import { flagMode, columnMode } from "@/lib/flagDensity";
import {
  ReplacementBadge,
  AssetStatusBadge,
  formatNlDate,
} from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

// A count that outgrew its badge comes back as a fact you can click.
const FactFilter = ({ label, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="underline decoration-dotted hover:text-white">
    {label}
  </button>
);

export default function AssetsList() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Filters/search persist in the URL so the back button restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const [fLoc, setFLoc] = useState(searchParams.get("loc") || "all");
  const [fCat, setFCat] = useState(searchParams.get("cat") || "all");
  const [fStatus, setFStatus] = useState(searchParams.get("status") || "all");
  const [fResp, setFResp] = useState(searchParams.get("resp") || "all");
  const [fReplace, setFReplace] = useState(searchParams.get("replace") || "all");
  const [fAanvullen, setFAanvullen] = useState(searchParams.get("aanvullen") === "1");
  // Round 22: after the McMain import a machine can be at a site without a
  // ruimte yet. That is work, so it is countable and clickable -- not hidden.
  const [fNoRuimte, setFNoRuimte] = useState(searchParams.get("zonderruimte") === "1");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    const p = {};
    if (fLoc !== "all") p.loc = fLoc;
    if (fCat !== "all") p.cat = fCat;
    if (fStatus !== "all") p.status = fStatus;
    if (fResp !== "all") p.resp = fResp;
    if (fReplace !== "all") p.replace = fReplace;
    if (fAanvullen) p.aanvullen = "1";
    if (fNoRuimte) p.zonderruimte = "1";
    if (search.trim()) p.q = search.trim();
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fLoc, fCat, fStatus, fResp, fReplace, fAanvullen, fNoRuimte, search]);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [a, l, c] = await Promise.all([
        api.get("/assets"),
        api.get("/locations"),
        api.get("/asset-categories"),
      ]);
      setAssets(a.data);
      setLocations(l.data);
      setCategories(c.data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Assets konden niet geladen worden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = assets.filter((a) => {
      if (fLoc !== "all" && (a.location_id || "__none__") !== fLoc) return false;
      if (fCat !== "all" && a.category_id !== fCat) return false;
      if (fStatus !== "all" && a.status !== fStatus) return false;
      if (fResp !== "all" && a.responsibility !== fResp) return false;
      if (fReplace !== "all" && a.derived.replacement_status !== fReplace) return false;
      if (fAanvullen && !a.derived.needs_completion) return false;
      if (fNoRuimte && a.ruimte_id) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [a.asset_code, a.name, a.brand, a.model, a.serial_number]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows = rows.slice().sort((x, y) => {
      const dx = x.derived.replacement_date || "9999-99-99";
      const dy = y.derived.replacement_date || "9999-99-99";
      return sortAsc ? dx.localeCompare(dy) : dy.localeCompare(dx);
    });
    return rows;
  }, [assets, fLoc, fCat, fStatus, fResp, fReplace, fAanvullen, fNoRuimte, search, sortAsc]);

  // Density is measured on the rows currently on screen, not on the whole set.
  const aanvullen = useMemo(() => flagMode(filtered, (a) => a.derived.needs_completion), [filtered]);
  const over = useMemo(() => flagMode(filtered, (a) => a.derived.replacement_status === "rood"), [filtered]);
  // The register is NAMED now ("Machine 1", "Slangen baan 2") and carries no
  // brand at all on the imported rows, so one column holds whatever the machine
  // is called.
  const nameCol = useMemo(() => columnMode(filtered, (a) => a.name || a.brand || a.model), [filtered]);
  const placeCol = useMemo(() => columnMode(filtered, (a) => a.ruimte_id), [filtered]);

  const facts = useMemo(() => {
    const base = [`${assets.length} assets`, `${locations.length} locaties`];
    // At most two flag-derived facts, highest density first.
    const flags = [
      aanvullen.mode === "summary" && {
        ratio: aanvullen.ratio,
        node: <FactFilter key="aanvullen" testid="fact-aanvullen" label={`${aanvullen.count} aanvullen`} onClick={() => setFAanvullen(true)} />,
      },
      over.mode === "summary" && {
        ratio: over.ratio,
        node: <FactFilter key="over" testid="fact-over" label={`${over.count} over vervangdatum`} onClick={() => setFReplace("rood")} />,
      },
      !nameCol.show && {
        ratio: nameCol.ratio,
        node: `${nameCol.missing} zonder naam`,
      },
      !placeCol.show && placeCol.missing > 0 && {
        ratio: 1 - placeCol.ratio,
        node: <FactFilter key="zonderruimte" testid="fact-zonder-ruimte" label={`${placeCol.missing} zonder ruimte`} onClick={() => setFNoRuimte(true)} />,
      },
    ].filter(Boolean).sort((a, b) => b.ratio - a.ratio).slice(0, 2).map((f) => f.node);
    // Three facts is the cap; the flags outrank the second base count.
    return flags.length >= 2 ? [base[0], ...flags] : [...base, ...flags];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.length, locations.length, aanvullen, over, nameCol, placeCol]);

  const actions = canWrite()
    ? [{ key: "nieuw", label: "Nieuw asset", icon: Plus, variant: "primary", testid: "assets-create-button", onClick: () => navigate("/assets/nieuw") }]
    : [];

  const AanvullenChip = () => (
    <span title="Gegevens onvolledig" className="ml-1 rounded bg-letop-vlak px-1 text-label text-letop-tekst">
      aanvullen
    </span>
  );

  const colCount = 6 + (nameCol.show ? 1 : 0) + (placeCol.show ? 1 : 0);
  // Zero rows means zero column headings: a head above nothing reads as a bug.
  const isEmpty = !loading && filtered.length === 0;
  const anyFilter = fLoc !== "all" || fCat !== "all" || fStatus !== "all" || fResp !== "all"
    || fReplace !== "all" || fAanvullen || fNoRuimte || Boolean(search.trim());

  return (
    <Page console={{ variant: "view", title: "Assets", facts, actions }}>
      {loadError ? (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <p data-testid="assets-load-error" className="text-body text-destructive">{loadError}</p>
          <Button variant="secondary" onClick={load}>Opnieuw proberen</Button>
        </Card>
      ) : (
        <Card>
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
            <div className="relative w-full sm:w-56">
              <Search size={18} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="assets-search-input" className="h-9 pl-8" placeholder="Zoek code, merk, model, serienr." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={fLoc} onValueChange={setFLoc}>
              <SelectTrigger className="h-9 w-40" data-testid="assets-filter-location"><SelectValue placeholder="Locatie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle locaties</SelectItem>
                <SelectItem value="__none__">Zonder locatie</SelectItem>
                {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fCat} onValueChange={setFCat}>
              <SelectTrigger className="h-9 w-40" data-testid="assets-filter-category"><SelectValue placeholder="Categorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieën</SelectItem>
                {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger className="h-9 w-36" data-testid="assets-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="actief">Actief</SelectItem>
                <SelectItem value="defect">Defect</SelectItem>
                <SelectItem value="in_reparatie">In reparatie</SelectItem>
                <SelectItem value="vervangen">Vervangen</SelectItem>
                <SelectItem value="afgevoerd">Afgevoerd</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fResp} onValueChange={setFResp}>
              <SelectTrigger className="h-9 w-40" data-testid="assets-filter-responsibility"><SelectValue placeholder="Verantwoordelijkheid" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle verantwoordelijkheid</SelectItem>
                <SelectItem value="ons">Ons</SelectItem>
                <SelectItem value="verhuurder">Verhuurder</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fReplace} onValueChange={setFReplace}>
              <SelectTrigger className="h-9 w-36" data-testid="assets-filter-replacement"><SelectValue placeholder="Vervangstatus" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle vervangstatus</SelectItem>
                <SelectItem value="groen">Groen</SelectItem>
                <SelectItem value="oranje">Oranje</SelectItem>
                <SelectItem value="rood">Rood</SelectItem>
                <SelectItem value="onbekend">Onbekend</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant={fAanvullen ? "default" : "secondary"}
              size="sm"
              data-testid="assets-filter-aanvullen"
              onClick={() => setFAanvullen((v) => !v)}
            >
              Aanvullen
            </Button>
            {/* Import tooling sits with the filters, never in the console. */}
            {canWrite() && <div className="ml-auto"><ImportAssets onImported={load} /></div>}
          </div>

          <div className="hidden sm:block">
            <Table data-testid="assets-table">
              {!isEmpty && (
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  {/* A column missing on a third of the rows does not render. */}
                  {nameCol.show && <TableHead>Naam</TableHead>}
                  <TableHead>Locatie</TableHead>
                  {/* Ruimte and spoor answer one question, so they share one
                      column: waar staat het. */}
                  {placeCol.show && <TableHead>Waar</TableHead>}
                  <TableHead>Categorie</TableHead>
                  <TableHead>Leeftijd</TableHead>
                  <TableHead>
                    <button className="inline-flex items-center gap-1" onClick={() => setSortAsc((v) => !v)} data-testid="assets-sort-replacement">
                      Vervangdatum <ArrowUpDown size={14} />
                    </button>
                  </TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              )}
              <TableBody>
                {loading && Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={colCount}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                ))}
                {!loading && filtered.map((a) => (
                  <TableRow key={a.id} data-testid={`asset-row-${a.asset_code}`} className="cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    <TableCell>
                      <Code>{a.asset_code}</Code>
                      {aanvullen.mode === "row" && a.derived.needs_completion && <AanvullenChip />}
                    </TableCell>
                    {nameCol.show && (
                      <TableCell className="text-foreground">{a.name || [a.brand, a.model].filter(Boolean).join(" ")}</TableCell>
                    )}
                    <TableCell>{a.location_name}</TableCell>
                    {placeCol.show && (
                      <TableCell className="text-muted-foreground">
                        {[a.ruimte_name, a.spoor].filter(Boolean).join(" · ")}
                      </TableCell>
                    )}
                    <TableCell>{a.category_name}</TableCell>
                    <TableCell className={a.derived.age_text === "onbekend" ? "text-inkt-zacht" : ""}>{a.derived.age_text}</TableCell>
                    <TableCell>
                      {over.mode === "row"
                        ? <ReplacementBadge status={a.derived.replacement_status} date={a.derived.replacement_date} />
                        : a.derived.replacement_date && <Code>{formatNlDate(a.derived.replacement_date)}</Code>}
                    </TableCell>
                    <TableCell><AssetStatusBadge status={a.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Phone: the same rows as tappable cards (identifier -> location -> status). */}
          <div className="sm:hidden" data-testid="assets-cardlist">
            {loading && Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-3"><Skeleton className="h-16 w-full" /></div>
            ))}
            {!loading && filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`asset-card-${a.asset_code}`}
                onClick={() => navigate(`/assets/${a.id}`)}
                className="flex min-h-[72px] w-full items-start justify-between gap-3 p-3 text-left odd:bg-zebra"
              >
                <div className="min-w-0 space-y-1">
                  <div>
                    <Code>{a.asset_code}</Code>
                    {aanvullen.mode === "row" && a.derived.needs_completion && <AanvullenChip />}
                  </div>
                  {(a.name || a.brand || a.model) && (
                    <div className="truncate text-body text-foreground">{a.name || [a.brand, a.model].filter(Boolean).join(" ")}</div>
                  )}
                  <div className="flex items-center gap-1 text-body text-muted-foreground">
                    <MapPin size={14} className="shrink-0" />
                    <span className="truncate">
                      {[a.location_name || "Zonder locatie", a.ruimte_name, a.spoor].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <div className="text-label text-muted-foreground">{a.category_name} · {a.derived.age_text}</div>
                  <div className="pt-1">
                    {over.mode === "row"
                      ? <ReplacementBadge status={a.derived.replacement_status} date={a.derived.replacement_date} />
                      : a.derived.replacement_date && <Code>{formatNlDate(a.derived.replacement_date)}</Code>}
                  </div>
                </div>
                <AssetStatusBadge status={a.status} />
              </button>
            ))}
          </div>

          {isEmpty && (
            <InlineEmpty
              testid="assets-empty"
              title={anyFilter ? "Geen asset past bij deze filters" : "Nog geen machines vastgelegd"}
              purpose={anyFilter
                ? "Dit is het register van elke machine, pomp en wagen: code, locatie, leeftijd en vervangdatum. Verruim je filters om er weer bij te komen."
                : "Dit is het register van elke machine, pomp en wagen: code, locatie, leeftijd en vervangdatum. Zet de eerste machine erin, dan rekent het systeem de vervanging voor je uit."}
              action={anyFilter
                ? { label: "Filters wissen", onClick: () => { setFLoc("all"); setFCat("all"); setFStatus("all"); setFResp("all"); setFReplace("all"); setFAanvullen(false); setFNoRuimte(false); setSearch(""); } }
                : (canWrite() ? { label: "Nieuw asset", icon: Plus, onClick: () => navigate("/assets/nieuw") } : null)}
            />
          )}
        </Card>
      )}
    </Page>
  );
}
